import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, AlertCircle, Loader2, Mail } from 'lucide-react';

/**
 * Magic-link landing page for email contact verification.
 *
 * Visited from the SendGrid magic-link email — query has ?rid=<request_id>&token=<jwt>.
 * Calls POST /api/contacts/verify and shows a success / failure card.
 *
 * Talent must already be logged in to verify (the JWT in localStorage
 * matches the user_id on the request). If logged out, we route them to
 * /login with a `next` param so they land back here.
 */
const VerifyContact = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const rid = params.get('rid');
  const token = params.get('token');

  const [state, setState] = useState('loading'); // loading | success | error | login_required
  const [message, setMessage] = useState('');

  useEffect(() => {
    const run = async () => {
      if (!rid || !token) {
        setState('error');
        setMessage('This verification link is incomplete. Please use the link from your email.');
        return;
      }
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        setState('login_required');
        return;
      }
      try {
        const res = await api.post('/contacts/verify', {
          request_id: rid,
          token,
        });
        setState('success');
        setMessage(res.data?.message || 'Email verified.');
      } catch (e) {
        setState('error');
        setMessage(e?.response?.data?.detail || 'Verification failed.');
      }
    };
    run();
  }, [rid, token]);

  const renderBody = () => {
    if (state === 'loading') {
      return (
        <div className="flex flex-col items-center text-center py-6 gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
          <p className="text-sm text-gray-700">Verifying your new email…</p>
        </div>
      );
    }
    if (state === 'login_required') {
      return (
        <div className="flex flex-col items-center text-center py-4 gap-4">
          <Mail className="w-10 h-10 text-cyan-600" />
          <p className="text-sm text-gray-700">
            Please log in to confirm this email change. We'll bring you right back here.
          </p>
          <Button
            onClick={() => navigate(`/login?next=${encodeURIComponent(`/verify-contact?rid=${rid}&token=${token}`)}`)}
            data-testid="verify-contact-login-btn"
          >
            Log in to confirm
          </Button>
        </div>
      );
    }
    if (state === 'success') {
      return (
        <div className="flex flex-col items-center text-center py-4 gap-3">
          <CheckCircle2 className="w-12 h-12 text-emerald-500" />
          <p className="text-base font-semibold text-gray-900" data-testid="verify-success-msg">
            Email verified
          </p>
          <p className="text-sm text-gray-600">{message}</p>
          <Button onClick={() => navigate('/talent/profile?contact_verified=email')} data-testid="verify-back-btn">
            Back to my profile
          </Button>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center text-center py-4 gap-3">
        <AlertCircle className="w-12 h-12 text-rose-500" />
        <p className="text-base font-semibold text-gray-900" data-testid="verify-error-msg">
          Verification failed
        </p>
        <p className="text-sm text-gray-600">{message}</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/talent/profile')} data-testid="verify-back-btn">
            Back to profile
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md" data-testid="verify-contact-page">
        <CardHeader>
          <CardTitle className="text-center">Confirm Email Change</CardTitle>
        </CardHeader>
        <CardContent>{renderBody()}</CardContent>
      </Card>
    </div>
  );
};

export default VerifyContact;
